/**
 * Executes the given async callback under an exclusive lock identified by `lockHandle`.
 *
 * If a callback is already running for the same `lockHandle`, this function
 * returns the existing in-flight Promise instead of invoking the callback again.
 * This ensures that only one execution per lock handle occurs at a time.
 *
 * The lock is automatically released once the Promise settles (either resolved or rejected).
 *
 * @template TValue The resolved value type returned by the callback.
 *
 * @param lockHandle A unique symbol used to identify the lock scope.
 *                   Calls using the same symbol share the same execution lock.
 * @param callback An async function returning a Promise to be executed exclusively.
 *
 * @returns A Promise resolving to the callback's result. If another execution
 *          is already in progress for the same lock, returns the existing Promise.
 *
 * @throws Propagates any error thrown or rejection returned by the callback.
 *
 * @example
 * const lock = Symbol('my-lock');
 *
 * await runCallbackWithExclusiveLock(lock, async () => {
 *   // This will only execute once at a time per lock
 *   return await fetchData();
 * });
 */
const locks: Record<symbol, Promise<any>> = {};
export async function runCallbackWithExclusiveLock<TValue>(
  lockHandle: symbol,
  callback: () => Promise<TValue>
): Promise<TValue> {
  if (!(lockHandle in locks)) {
    locks[lockHandle] = new Promise<TValue>((resolve, reject) => {
      callback()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          delete locks[lockHandle];
        });
    });
  }

  return locks[lockHandle];
}
